import { defineStore } from 'pinia'
import type { ClientPrincipal } from '@/utils/Authentication'
import { fetchUserInfo } from '@/utils/Authentication'
import { GET_USER_INFO } from '@/utils/Queries'
import client from '@/utils/ApolloClient'
import { clearTokenExpirationCheck, scheduleExpirationCheckFromExp } from '@/utils/TokenManager'
import { handleSessionError } from '@/utils/ErrorHandler'

export const useMainStore = defineStore('mainStore', {
  state: () => ({
    clientPrincipal: {} as ClientPrincipal,
    userName: '',
    orgName: '',
    fullName: '',
    showPartnersProjects: false,
    showPartnersProspecting: false,
    focusListenerActive: false
  }),

  /** GETTERS **/
  getters: {
    // Returns whether the user has 'authenticated' in their userRoles array
    isAuthenticated: (state) => {
      return (state.clientPrincipal?.userRoles ?? []).includes('authenticated')
    },
    // Checks if the user is a 'prospector'
    isProspector: (state) => {
      return (state.clientPrincipal?.userRoles ?? []).includes('prospector')
    },
    // Check if the user is an admin
    isAdmin: (state) => {
      return (state.clientPrincipal?.userRoles ?? []).includes('norhybrid_admin')
    },
    //Shortcut for userId
    userId: (state) => {
      return state.clientPrincipal?.userId || ''
    }
  },

  actions: {
    /**
     * Refresh session info by calling `/.auth/me` and setting store properties.
     * Also fetches extra data via GraphQL.
     */
    async refresh() {
      try {
        const isDevEnvironment = import.meta.env.VITE_APP_VERSION === 'DEV'
        const response = await fetchUserInfo()

        const hasIdentity = Boolean(
          response.userId || response.userDetails || response.identityProvider
        )
        const hasAuthenticatedRole = (response.userRoles ?? []).includes('authenticated')
        const isRolesMissing = hasIdentity && !hasAuthenticatedRole
        const isInvalidSession = !hasIdentity || (!isDevEnvironment && !response.claims)

        if (isInvalidSession) {
          handleSessionError(
            "We couldn't validate your permissions after renewing your session. Please refresh to re-authenticate. If the problem persists, log out and back in.",
            true,
            0,
            'Refresh',
            true
          )
          return
        }

        if (isRolesMissing) {
          // `fetchUserInfo()` already retries briefly; if we still get here, treat as recovery required.
          handleSessionError(
            "We couldn't validate your permissions after renewing your session. Please refresh to re-authenticate. If the problem persists, log out and back in.",
            !isDevEnvironment,
            0,
            'Refresh',
            true
          )
          return
        }

        this.clientPrincipal = response

        // Schedule token expiration check
        this.scheduleExpirationOnFocus()

        // If userDetails includes '#', extract name from that
        if (response.userDetails?.includes('#')) {
          this.userName = response.userDetails.split('#')[1]
        } else {
          this.userName = response.userDetails ?? ''
        }

        // Fetch additional user information using GraphQL (only when authenticated)
        if (hasAuthenticatedRole) {
          const gql = await client.query({
            query: GET_USER_INFO,
            variables: {
              filter: { ObjectId: { eq: response.userId } }
            },
            context: {
              requestRole: 'authenticated'
            }
          })

          if (gql.data && gql.data.users) {
            this.orgName = gql.data.users.items[0].Organization.Name
            this.fullName = gql.data.users.items[0].Name
          }
        }
      } catch (error) {
        console.error('Session validation failed:', error)
        // Optionally: Call handleSessionError('Session invalid'); to redirect if you want
      }
    },

    //Re-schedule token expiration check based on the current session state.
    scheduleExpirationOnFocus() {
      const clientPrincipal = this.clientPrincipal

      if (clientPrincipal?.claims) {
        const expClaim = clientPrincipal.claims.find((claim) => claim.typ === 'exp')
        if (expClaim) {
          const exp = parseInt(expClaim.val, 10) // Parse expiration timestamp
          if (!isNaN(exp)) {
            scheduleExpirationCheckFromExp(exp) // Schedule the new expiration check
          } else {
            console.warn('Invalid exp claim value in token.')
          }
        } else {
          console.warn('No exp claim found in token.')
        }
      } else {
        console.warn('No token claims found in clientPrincipal.')
      }
    },

    // Add focus listener for session expiration
    addFocusListener() {
      if (this.focusListenerActive) {
        return
      }

      const focusHandler = () => {
        this.scheduleExpirationOnFocus() // Re-schedule expiration check
      }

      window.addEventListener('focus', focusHandler)
      this.focusListenerActive = true
    },

    // Remove focus listener for session expiration
    removeFocusListener() {
      if (!this.focusListenerActive) {
        return
      }

      window.removeEventListener('focus', this.scheduleExpirationOnFocus as EventListener)
      this.focusListenerActive = false
    },

    // Clear cookies, session storage, and local store data for logout
    async logout() {
      try {
        clearTokenExpirationCheck() // Clear token expiration timeout
        this.removeFocusListener() // Clear the focus listener
        this.$reset() // Reset pinia store

        // Clear cookies
        document.cookie.split(';').forEach((c) => {
          const [key] = c.trim().split('=')
          document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${location.hostname}`
        })

        sessionStorage.clear()
        localStorage.clear()
      } catch (error) {
        console.error('Logout process failed:', error)
      }
    }
  }
})
